import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import CurrencyFormat from "react-currency-format";
import { Tooltip } from "react-tooltip";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import MenuButton from "../MenuButton/MenuButton";
import useTable from "../../hooks/useTable";
import useSortableData from "../../hooks/useSortableData";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";

const SERVICE_OPTIONS = [
  { id: "6", label: "Sell Registration Package", shortLabel: "Registration" },
  { id: "1", label: "Sell Gym Package", shortLabel: "Gym" },
  { id: "2", label: "Sell Trainer Package", shortLabel: "Trainer" },
  { id: "3", label: "Sell Guest Pass Package", shortLabel: "Guest Pass" },
  { id: "4", label: "Sell Small Group PT Package", shortLabel: "Small Group PT" },
  { id: "16", label: "Sell Befit Package", shortLabel: "Befit" },
  { id: "15", label: "Sell GX Package", shortLabel: "GX" },
  { id: "5", label: "Sell Nutrition Package", shortLabel: "Nutrition" },
  { id: "7", label: "Sell Bootcamp Package", shortLabel: "Bootcamp" },
  { id: "12", label: "Sell Massage Chair Service", shortLabel: "Massage Chair" },
  { id: "8", label: "Sell Freezing", shortLabel: "Freezing" },
  { id: "9", label: "Sell General Package", shortLabel: "General" },
  { id: "11", label: "Sell CFT Package", shortLabel: "CFT" },
  { id: "14", label: "Sell Physiotherapy Package", shortLabel: "Physiotherapy" },
];

const GROUP_OPTIONS = [
  { id: "MWF", name: "Group MWF" },
  { id: "TTS", name: "Group TTS" },
];

const todayValue = () => new Date().toLocaleDateString("en-CA");

const buildInitialForm = () => {
  const today = todayValue();

  return {
    memType: "",
    gymPkgType: "",
    trainer: "",
    trainerPkgType: "",
    pkgType: "",
    qty: "1",
    discount_type: "Amount",
    discount_amount: "0",
    pay_after_dis: "0",
    sdate: today,
    approveBy: "",
    referencedBy: "",
    salesnotes: "",
    salesdate: today,
    Validilty: "",
    total_fee: "",
    Group: "",
    ptPackages: "",
    time_slot: "",
    Nutritionist: "",
    NutritionistPkgType: "",
    order_id: "",
    duration: "",
  };
};

const parseNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isFilled = (value) => `${value ?? ""}`.trim() !== "";

const toSaleType = (search, type) => {
  const params = new URLSearchParams(search);
  const queryValue = params.get("saleType");

  if (queryValue === "Renew" || queryValue === "New") {
    return queryValue;
  }

  if (type === "renew") {
    return "Renew";
  }

  if (type === "new") {
    return "New";
  }

  return "New";
};

const getServiceLabel = (serviceId) =>
  SERVICE_OPTIONS.find((service) => service.id === serviceId)?.shortLabel || "Package";

export default function PackageSell() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const { id, type } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [cart, setCart] = useState(false);
  const [clientName, setClientName] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [saleType, setSaleType] = useState(toSaleType(location.search, type));
  const [myData, setMyData] = useState([]);
  const [limit] = useState("10");
  const [catagrieAdded, setCatagrieAdded] = useState([]);

  const [form, setForm] = useState(buildInitialForm());
  const [fieldErrors, setFieldErrors] = useState({});
  const [discountError, setDiscountError] = useState("");
  const [priceError, setPriceError] = useState("");
  const [serviceMessage, setServiceMessage] = useState("");
  const [gxFull, setGxFull] = useState(false);
  const [hasActiveGymPackage, setHasActiveGymPackage] = useState(true);

  const [primaryOptions, setPrimaryOptions] = useState([]);
  const [secondaryOptions, setSecondaryOptions] = useState([]);
  const [tertiaryOptions, setTertiaryOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);
  const [timeSlotOptions, setTimeSlotOptions] = useState([]);
  const [freezingOrders, setFreezingOrders] = useState([]);

  const [basePrice, setBasePrice] = useState("0");
  const [standardPrice, setStandardPrice] = useState(0);
  const [endDate, setEndDate] = useState(todayValue());
  const [duration, setDuration] = useState("0");
  const [fixedPackageId, setFixedPackageId] = useState("");
  const [fixedPackageName, setFixedPackageName] = useState("");

  const resetDynamicState = (keepDates = false) => {
    const nextForm = buildInitialForm();

    setForm((prev) => ({
      ...nextForm,
      salesdate: prev.salesdate || todayValue(),
      sdate: keepDates ? prev.sdate : nextForm.sdate,
    }));
    setFieldErrors({});
    setDiscountError("");
    setPriceError("");
    setServiceMessage("");
    setGxFull(false);
    setHasActiveGymPackage(true);
    setPrimaryOptions([]);
    setSecondaryOptions([]);
    setTertiaryOptions([]);
    setProviderOptions([]);
    setTimeSlotOptions([]);
    setFreezingOrders([]);
    setBasePrice("0");
    setStandardPrice(0);
    setEndDate(todayValue());
    setDuration("0");
    setFixedPackageId("");
    setFixedPackageName("");
  };

  const applyDiscount = (
    rawBasePrice,
    discountType = form.discount_type,
    rawDiscountAmount = form.discount_amount
  ) => {
    const currentBasePrice = parseNumber(rawBasePrice);
    const discountAmount = isFilled(rawDiscountAmount) ? parseNumber(rawDiscountAmount) : 0;

    if (discountAmount < 0) {
      setDiscountError("Maximum discount must be equal to or greater than 0");
      setForm((prev) => ({ ...prev, pay_after_dis: "0" }));
      return;
    }

    if (discountType === "Percentage" && discountAmount > 100) {
      setDiscountError("Maximum discount must be equal to or less than 100%");
      setForm((prev) => ({ ...prev, pay_after_dis: "0" }));
      return;
    }

    if (discountType !== "Percentage" && discountAmount > currentBasePrice) {
      setDiscountError(
        `Maximum discount must be equal to or less than Package Full Price: ${currentBasePrice}`
      );
      setForm((prev) => ({ ...prev, pay_after_dis: "0" }));
      return;
    }

    const netPrice =
      discountType === "Percentage"
        ? currentBasePrice - (currentBasePrice / 100) * discountAmount
        : currentBasePrice - discountAmount;

    setDiscountError("");
    setForm((prev) => ({
      ...prev,
      pay_after_dis: `${Math.max(0, Number(netPrice.toFixed(2)))}`,
    }));
  };

  const updateBasePrice = (value, nextStandardPrice = standardPrice, validateEditable = false) => {
    const numericValue = parseNumber(value);

    setBasePrice(`${numericValue}`);

    if (validateEditable && nextStandardPrice > 0) {
      const minPrice = nextStandardPrice - 10000;

      if (numericValue < minPrice) {
        setPriceError(
          `Actual package price is ${nextStandardPrice}. Cannot be less than ${minPrice}`
        );
      } else {
        setPriceError("");
      }
    } else {
      setPriceError("");
    }

    applyDiscount(numericValue, form.discount_type, form.discount_amount);
  };

  const mapOptions = (records, labelGetter = (item) => item.name, valueGetter = (item) => item.id) =>
    records.map((item) => ({
      id: `${valueGetter(item) ?? ""}`,
      name: labelGetter(item),
    }));

  const fetchClientName = async () => {
    try {
      const response = await UserServices.getClientsNameById(branch_id, id);
      const record = response.data?.data?.[0];

      setClientName(
        record ? `${record.first_name || ""} ${record.last_name || ""}`.trim() : ""
      );
    } catch (error) {
      console.log(error);
      setClientName("");
    }
  };

  const getCart = async () => {
    try {
      const response = await UserServices.getClientsCart(id);
      const cartItems = response.data?.data?.data || [];

      const results = cartItems.map((value) => ({
        id: value.id,
        name: `${value.client_fname || ""} ${value.client_lname || ""}`.trim(),
        package_name: value.package_name,
        price: parseNumber(value.price),
        discount: parseNumber(value.discount),
        net_price: parseNumber(value.price_with_tax),
        note: value.note,
        tax: parseNumber(value.tax),
      }));

      setCart(results.length > 0);
      setMyData(results);
      setSelectedService("");
      resetDynamicState();
    } catch (error) {
      console.log(error);
      setCart(false);
      setMyData([]);
    }
  };

  const getCatagriesAdded = async () => {
    try {
      const response = await UserServices.getclientCatagriesList(id);
      setCatagrieAdded(response.data || []);
    } catch (error) {
      console.log(error);
      setCatagrieAdded([]);
    }
  };

  const loadCommonPageData = async () => {
    setLoading(true);
    await Promise.all([fetchClientName(), getCart(), getCatagriesAdded()]);
    setLoading(false);
  };

  useEffect(() => {
    setSaleType(toSaleType(location.search, type));
  }, [location.search, type]);

  useEffect(() => {
    loadCommonPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, branch_id]);

  const loadServiceData = async (serviceId) => {
    if (!serviceId) {
      return;
    }

    setLoading(true);
    setFieldErrors({});
    setServiceMessage("");

    try {
      if (serviceId === "1") {
        const response = await UserServices.membershipType(branch_id, "6");
        setPrimaryOptions([
          { id: "", name: "Select Membership Type" },
          ...mapOptions(response.data?.data || []),
        ]);
      } else if (serviceId === "2") {
        const response = await UserServices.TrainerName(branch_id);
        setProviderOptions([
          { id: "", name: "Select Trainer" },
          ...mapOptions(
            response.data?.data || [],
            (item) => `${item.first_name || ""} ${item.last_name || ""}`.trim()
          ),
        ]);
      } else if (serviceId === "3") {
        const response = await UserServices.registrationPkgs(branch_id, "3");
        const packages = response.data?.data || [];

        setFixedPackageId(`${packages[0]?.id || ""}`);
        setFixedPackageName(packages[0]?.name || "Guest Pass");
      } else if (serviceId === "4") {
        try {
          await UserServices.getClientActiveGymPkg(id);
          setHasActiveGymPackage(true);
        } catch (error) {
          console.log(error);
          setHasActiveGymPackage(false);
          setServiceMessage("An active gym package is required before selling a small group PT package.");
        }
      } else if (serviceId === "5") {
        const response = await UserServices.NutritionistName(branch_id);
        setProviderOptions([
          { id: "", name: "Select Nutritionist" },
          ...mapOptions(
            response.data?.data || [],
            (item) => `${item.first_name || ""} ${item.last_name || ""}`.trim()
          ),
        ]);
      } else if (serviceId === "6") {
        const response = await UserServices.registrationPkgs(branch_id, "6");
        setPrimaryOptions([
          { id: "", name: "Select Membership Type" },
          ...mapOptions(response.data?.data || []),
        ]);
      } else if (serviceId === "7") {
        const response = await UserServices.registrationPkgs(branch_id, "7");
        setPrimaryOptions([
          { id: "", name: "Select Session" },
          ...mapOptions(response.data?.data || []),
        ]);
      } else if (serviceId === "8") {
        const [ordersResponse, packageResponse] = await Promise.all([
          UserServices.checkClientName(id),
          UserServices.freezingPkgName(branch_id),
        ]);

        const orders = ordersResponse.data?.data || [];
        const packages = packageResponse.data?.data?.data || [];
        const firstPackage = packages[0];

        setFreezingOrders([
          { id: "", name: "Select Order" },
          ...mapOptions(orders, (item) => item.package_name, (item) => item.order_id),
        ]);
        setFixedPackageId(`${firstPackage?.id || ""}`);
        setFixedPackageName(firstPackage?.package_name || "");
        updateBasePrice(firstPackage?.price || 0);
      } else if (serviceId === "9") {
        const response = await UserServices.registrationPkgs(branch_id, "9");
        setPrimaryOptions([
          { id: "", name: "Select Package" },
          ...mapOptions(response.data?.data || []),
        ]);
      } else if (serviceId === "11") {
        const response = await UserServices.registrationPkgs(branch_id, "11");
        setPrimaryOptions([
          { id: "", name: "Select Package" },
          ...mapOptions(response.data?.data || []),
        ]);
      } else if (serviceId === "12") {
        const response = await UserServices.registrationPkgs(branch_id, "12");
        setPrimaryOptions([
          { id: "", name: "Select Package" },
          ...mapOptions(response.data?.data || []),
        ]);
      } else if (serviceId === "14") {
        const response = await UserServices.membershipType(branch_id, "14");
        setPrimaryOptions([
          { id: "", name: "Select Physiotherapy Package" },
          ...mapOptions(response.data?.data || []),
        ]);
      } else if (serviceId === "15") {
        const response = await UserServices.registrationPkgs(branch_id, "15");
        setPrimaryOptions([
          { id: "", name: "Select Class" },
          ...mapOptions(response.data?.data || []),
        ]);
      } else if (serviceId === "16") {
        const response = await UserServices.befitPkgTypesList("16");
        const firstPackage = response.data?.data?.[0];

        setFixedPackageId(`${firstPackage?.id || ""}`);
        setFixedPackageName(firstPackage?.name || "");
        setForm((prev) => ({
          ...prev,
          pkgType: firstPackage?.name || "",
        }));

        if (firstPackage?.id) {
          const packageInfo = await UserServices.pkgFee(firstPackage.id, form.sdate, form.qty);
          setEndDate(packageInfo.data?.endDate || form.sdate);
        }
      }
    } catch (error) {
      console.log(error);
      setServiceMessage("Unable to load service details right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedService) {
      loadServiceData(selectedService);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService]);

  const onServiceChange = (event) => {
    const nextService = event.target.value;
    resetDynamicState();
    setSelectedService(nextService === "Select" ? "" : nextService);
  };

  const onDiscountTypeChange = (event) => {
    const nextType = event.target.value;

    setForm((prev) => ({
      ...prev,
      discount_type: nextType,
      discount_amount: "0",
    }));
    applyDiscount(basePrice, nextType, 0);
  };

  const onDiscountAmountChange = (event) => {
    const nextAmount = event.target.value;

    setForm((prev) => ({
      ...prev,
      discount_amount: nextAmount,
    }));
    applyDiscount(basePrice, form.discount_type, nextAmount);
  };

  const onSimpleFieldChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const loadPackageInfo = async (packageId, nextStartDate, quantity = 1) => {
    const response = await UserServices.pkgFee(packageId, nextStartDate, quantity);

    setEndDate(response.data?.endDate || nextStartDate);
    setDuration(`${response.data?.duration || "0"}`);

    return parseNumber(response.data?.price);
  };

  const onGymMembershipChange = async (event) => {
    const membershipId = event.target.value;

    setForm((prev) => ({
      ...prev,
      memType: membershipId,
      gymPkgType: "",
      discount_amount: "0",
      pay_after_dis: "0",
    }));
    setSecondaryOptions([]);
    updateBasePrice(0);

    if (!membershipId) {
      return;
    }

    try {
      setLoading(true);
      const response = await UserServices.gymPkgList(branch_id, "1", membershipId);
      setSecondaryOptions([
        { id: "", name: "Select Gym Package" },
        ...mapOptions(response.data?.data || []),
      ]);
    } catch (error) {
      console.log(error);
      setSecondaryOptions([{ id: "", name: "No Gym Packages Found" }]);
    } finally {
      setLoading(false);
    }
  };

  const onGymPackageChange = async (event) => {
    const packageId = event.target.value;

    setForm((prev) => ({
      ...prev,
      gymPkgType: packageId,
      discount_amount: "0",
      pay_after_dis: "0",
    }));

    if (!packageId) {
      updateBasePrice(0);
      return;
    }

    try {
      setLoading(true);
      const price = await loadPackageInfo(packageId, form.sdate, 1);
      setStandardPrice(price);
      updateBasePrice(price, price, true);
    } catch (error) {
      console.log(error);
      updateBasePrice(0);
      setPriceError("Error fetching package price");
    } finally {
      setLoading(false);
    }
  };

  const onRegistrationPackageChange = async (event) => {
    const packageId = event.target.value;

    setForm((prev) => ({
      ...prev,
      memType: packageId,
      discount_amount: "0",
      pay_after_dis: "0",
    }));

    if (!packageId) {
      updateBasePrice(0);
      return;
    }

    try {
      setLoading(true);
      const response = await UserServices.pkgFee(packageId, todayValue(), 1);
      const price = parseNumber(response.data?.price);
      setStandardPrice(price);
      updateBasePrice(price, price, selectedService === "6");
    } catch (error) {
      console.log(error);
      updateBasePrice(0);
      setPriceError("Error fetching package price");
    } finally {
      setLoading(false);
    }
  };

  const onDatePackageChange = async (event) => {
    const packageId = event.target.value;

    setForm((prev) => ({
      ...prev,
      memType: packageId,
      discount_amount: "0",
      pay_after_dis: "0",
    }));

    if (!packageId) {
      updateBasePrice(0);
      return;
    }

    if (selectedService === "15") {
      try {
        setLoading(true);
        const space = await UserServices.checkGfxSpace(packageId);

        if (!space.data?.is_space_available) {
          setGxFull(true);
          Swal.fire({
            title: "Error!",
            text: "Class space is full",
            icon: "error",
            confirmButtonColor: "#ed1c24",
          });
          setForm((prev) => ({ ...prev, memType: "" }));
          updateBasePrice(0);
          return;
        }

        setGxFull(false);
      } catch (error) {
        console.log(error);
        setGxFull(true);
        Swal.fire({
          title: "Error!",
          text: "Class space is full",
          icon: "error",
          confirmButtonColor: "#ed1c24",
        });
        setForm((prev) => ({ ...prev, memType: "" }));
        updateBasePrice(0);
        return;
      } finally {
        setLoading(false);
      }
    }

    try {
      setLoading(true);
      const price = await loadPackageInfo(packageId, form.sdate, 1);
      updateBasePrice(price);
    } catch (error) {
      console.log(error);
      updateBasePrice(0);
    } finally {
      setLoading(false);
    }
  };

  const onStartDateChange = async (event) => {
    const nextStartDate = event.target.value;

    setForm((prev) => ({ ...prev, sdate: nextStartDate }));

    if (!nextStartDate) {
      setEndDate(todayValue());
      return;
    }

    try {
      if (selectedService === "3" && isFilled(form.Validilty)) {
        setLoading(true);
        const response = await UserServices.validityDates(form.Validilty, nextStartDate);
        setEndDate(response.data?.endDate || nextStartDate);
        return;
      }

      if (selectedService === "4") {
        const startDate = new Date(nextStartDate);
        const nextEndDate = new Date(startDate);
        nextEndDate.setDate(startDate.getDate() + 30);
        setEndDate(nextEndDate.toLocaleDateString("en-CA"));
        return;
      }

      if (selectedService === "8") {
        const startDate = new Date(nextStartDate);
        const nextEndDate = new Date(startDate);
        nextEndDate.setDate(startDate.getDate() + parseNumber(form.duration));
        setEndDate(nextEndDate.toLocaleDateString("en-CA"));
        return;
      }

      let packageId = "";
      let quantity = 1;

      if (selectedService === "1") {
        packageId = form.gymPkgType;
      } else if (selectedService === "2") {
        packageId = form.pkgType;
        quantity = parseNumber(form.qty || 1);
      } else if (selectedService === "5") {
        packageId = form.pkgType;
      } else if (
        selectedService === "7" ||
        selectedService === "11" ||
        selectedService === "12" ||
        selectedService === "14" ||
        selectedService === "15"
      ) {
        packageId = form.memType;
      } else if (selectedService === "16") {
        packageId = fixedPackageId;
        quantity = parseNumber(form.qty || 1);
      }

      if (!packageId) {
        return;
      }

      setLoading(true);
      const price = await loadPackageInfo(packageId, nextStartDate, quantity);

      if (selectedService !== "16") {
        updateBasePrice(price, standardPrice, selectedService === "1");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const onEditablePriceChange = (event) => {
    const nextPrice = event.target.value;
    updateBasePrice(nextPrice, standardPrice, true);
  };

  const onGuestValidityChange = async (event) => {
    const validity = event.target.value;

    setForm((prev) => ({
      ...prev,
      Validilty: validity,
    }));

    if (!validity) {
      setEndDate(form.sdate);
      return;
    }

    try {
      setLoading(true);
      const response = await UserServices.validityDates(validity, form.sdate);
      setEndDate(response.data?.endDate || form.sdate);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const onGuestPriceChange = (event) => {
    setForm((prev) => ({
      ...prev,
      total_fee: event.target.value,
    }));
    updateBasePrice(event.target.value);
  };

  const onProviderChange = async (event) => {
    const providerId = event.target.value;
    const providerField = selectedService === "2" ? "trainer" : "Nutritionist";
    const packageTypeField = selectedService === "2" ? "trainerPkgType" : "NutritionistPkgType";

    setForm((prev) => ({
      ...prev,
      [providerField]: providerId,
      [packageTypeField]: "",
      pkgType: "",
      qty: "1",
      discount_amount: "0",
      pay_after_dis: "0",
    }));
    setSecondaryOptions([]);
    setTertiaryOptions([]);
    updateBasePrice(0);

    if (!providerId) {
      return;
    }

    try {
      setLoading(true);
      const response = await UserServices.trainerPkgList(providerId);
      setSecondaryOptions([
        { id: "", name: "Select Package Type" },
        ...mapOptions(response.data?.data || [], (item) => item.name, (item) => item.category_id),
      ]);
    } catch (error) {
      console.log(error);
      setSecondaryOptions([{ id: "", name: "No Package Type Found" }]);
    } finally {
      setLoading(false);
    }
  };

  const onProviderPackageTypeChange = async (event) => {
    const membershipId = event.target.value;
    const providerId = selectedService === "2" ? form.trainer : form.Nutritionist;
    const packageTypeField = selectedService === "2" ? "trainerPkgType" : "NutritionistPkgType";
    const categoryId = selectedService === "2" ? "2" : "5";

    setForm((prev) => ({
      ...prev,
      [packageTypeField]: membershipId,
      pkgType: "",
      qty: "1",
      discount_amount: "0",
      pay_after_dis: "0",
    }));
    setTertiaryOptions([]);
    updateBasePrice(0);

    if (!membershipId || !providerId) {
      return;
    }

    try {
      setLoading(true);
      const response = await UserServices.trainerPkgTypesList(
        branch_id,
        providerId,
        categoryId,
        membershipId
      );
      setTertiaryOptions([
        { id: "", name: "Select Package" },
        ...mapOptions(response.data?.data || []),
      ]);
    } catch (error) {
      console.log(error);
      setTertiaryOptions([{ id: "", name: "No Package Found" }]);
    } finally {
      setLoading(false);
    }
  };

  const onTrainerPackageChange = async (event) => {
    const packageId = event.target.value;
    const quantity = parseNumber(form.qty || 1);

    setForm((prev) => ({
      ...prev,
      pkgType: packageId,
      discount_amount: "0",
      pay_after_dis: "0",
    }));

    if (!packageId) {
      updateBasePrice(0);
      return;
    }

    try {
      setLoading(true);
      const price = await loadPackageInfo(packageId, form.sdate, quantity);
      updateBasePrice(price);
    } catch (error) {
      console.log(error);
      updateBasePrice(0);
    } finally {
      setLoading(false);
    }
  };

  const onTrainerQuantityChange = async (event) => {
    const quantity = event.target.value;

    setForm((prev) => ({
      ...prev,
      qty: quantity,
    }));

    if (!form.pkgType || !isFilled(quantity)) {
      return;
    }

    try {
      setLoading(true);
      const price = await loadPackageInfo(form.pkgType, form.sdate, quantity);
      updateBasePrice(price);
    } catch (error) {
      console.log(error);
      updateBasePrice(0);
    } finally {
      setLoading(false);
    }
  };

  const onGroupChange = async (event) => {
    const group = event.target.value;

    setForm((prev) => ({
      ...prev,
      Group: group,
      ptPackages: "",
      time_slot: "",
      total_fee: "",
      discount_amount: "0",
      pay_after_dis: "0",
    }));
    setSecondaryOptions([]);
    setTimeSlotOptions([]);
    updateBasePrice(0);

    if (!group) {
      return;
    }

    try {
      setLoading(true);
      const response = await UserServices.ptGroupPkgs(branch_id, "4", group);
      setSecondaryOptions([
        { id: "", name: "Select Package" },
        ...mapOptions(response.data?.data || []),
      ]);
    } catch (error) {
      console.log(error);
      setSecondaryOptions([{ id: "", name: "No Package Found" }]);
    } finally {
      setLoading(false);
    }
  };

  const onGroupPackageChange = async (event) => {
    const packageId = event.target.value;

    setForm((prev) => ({
      ...prev,
      ptPackages: packageId,
      time_slot: "",
      total_fee: "",
      discount_amount: "0",
      pay_after_dis: "0",
    }));
    setTimeSlotOptions([]);
    updateBasePrice(0);

    if (!packageId) {
      return;
    }

    try {
      setLoading(true);
      const response = await UserServices.timeSlot(branch_id, packageId);
      const slots = response.data?.data || [];
      const firstSlot = slots[0];

      setTimeSlotOptions(
        mapOptions(
          slots,
          (item) => `${item.start_time || ""} TO ${item.end_time || ""}`,
          (item) => item.id
        )
      );
      setForm((prev) => ({
        ...prev,
        time_slot: `${firstSlot?.id || ""}`,
        total_fee: `${firstSlot?.price || ""}`,
      }));
      updateBasePrice(firstSlot?.price || 0);
    } catch (error) {
      console.log(error);
      setTimeSlotOptions([{ id: "", name: "No Time Slot Found" }]);
      updateBasePrice(0);
    } finally {
      setLoading(false);
    }
  };

  const onTimeSlotChange = (event) => {
    setForm((prev) => ({
      ...prev,
      time_slot: event.target.value,
    }));
  };

  const onFreezingDurationChange = (event) => {
    const nextDuration = event.target.value;
    const startDate = new Date(form.sdate);
    const nextEndDate = new Date(startDate);

    nextEndDate.setDate(startDate.getDate() + parseNumber(nextDuration));

    setForm((prev) => ({
      ...prev,
      duration: nextDuration,
    }));
    setDuration(nextDuration);
    setEndDate(nextEndDate.toLocaleDateString("en-CA"));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!saleType) {
      nextErrors.saleType = "Required";
    }

    if (!selectedService) {
      nextErrors.service = "Required";
    }

    if (!form.salesdate) {
      nextErrors.salesdate = "Required";
    }

    if (selectedService === "1") {
      if (!form.memType) nextErrors.memType = "Required";
      if (!form.gymPkgType) nextErrors.gymPkgType = "Required";
      if (!form.sdate) nextErrors.sdate = "Required";
    } else if (selectedService === "2") {
      if (!form.trainer) nextErrors.trainer = "Required";
      if (!form.trainerPkgType) nextErrors.trainerPkgType = "Required";
      if (!form.pkgType) nextErrors.pkgType = "Required";
      if (!form.sdate) nextErrors.sdate = "Required";
      if (parseNumber(form.qty) < 1) nextErrors.qty = "Required";
    } else if (selectedService === "3") {
      if (parseNumber(form.Validilty) < 1) nextErrors.Validilty = "Required";
      if (!form.sdate) nextErrors.sdate = "Required";
      if (parseNumber(basePrice) < 0) nextErrors.total_fee = "Required";
    } else if (selectedService === "4") {
      if (!hasActiveGymPackage) nextErrors.service = "Active gym package required";
      if (!form.Group) nextErrors.Group = "Required";
      if (!form.ptPackages) nextErrors.ptPackages = "Required";
      if (!form.time_slot) nextErrors.time_slot = "Required";
      if (!form.sdate) nextErrors.sdate = "Required";
      if (parseNumber(basePrice) < 1) nextErrors.total_fee = "Required";
    } else if (selectedService === "5") {
      if (!form.Nutritionist) nextErrors.Nutritionist = "Required";
      if (!form.NutritionistPkgType) nextErrors.NutritionistPkgType = "Required";
      if (!form.pkgType) nextErrors.pkgType = "Required";
      if (!form.sdate) nextErrors.sdate = "Required";
    } else if (selectedService === "6") {
      if (!form.memType) nextErrors.memType = "Required";
    } else if (selectedService === "7" || selectedService === "11" || selectedService === "12") {
      if (!form.memType) nextErrors.memType = "Required";
      if (!form.sdate) nextErrors.sdate = "Required";
    } else if (selectedService === "8") {
      if (!form.order_id) nextErrors.order_id = "Required";
      if (!form.sdate) nextErrors.sdate = "Required";
      if (!endDate) nextErrors.end_date = "Required";
      if (parseNumber(basePrice) < 1) nextErrors.price = "Required";
      if (!form.duration || parseNumber(form.duration) < 2 || parseNumber(form.duration) > 100) {
        nextErrors.duration = "Please enter a value between 2 and 100";
      }
    } else if (selectedService === "9") {
      if (!form.memType) nextErrors.memType = "Required";
    } else if (selectedService === "14") {
      if (!form.memType) nextErrors.memType = "Required";
      if (!form.sdate) nextErrors.sdate = "Required";
    } else if (selectedService === "15") {
      if (!form.memType) nextErrors.memType = "Required";
      if (!form.sdate) nextErrors.sdate = "Required";
      if (gxFull) nextErrors.memType = "Class space is full";
    } else if (selectedService === "16") {
      if (!fixedPackageId) nextErrors.pkgType = "Required";
      if (!form.sdate) nextErrors.sdate = "Required";
    }

    if (
      selectedService !== "16" &&
      selectedService !== "8" &&
      selectedService !== "3" &&
      selectedService !== "4" &&
      parseNumber(basePrice) < 1
    ) {
      nextErrors.price = "Price is required";
    }

    if (priceError) {
      nextErrors.price = priceError;
    }

    if (discountError) {
      nextErrors.discount_amount = discountError;
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const addSelectedService = async () => {
    if (!validateForm()) {
      return;
    }

    const commonValues = {
      discount_type: form.discount_type,
      discount_amount: form.discount_amount,
      pay_after_dis: form.pay_after_dis,
      sdate: form.sdate,
      approveBy: form.approveBy,
      referencedBy: form.referencedBy,
      salesnotes: form.salesnotes,
      salesdate: form.salesdate,
      qty: form.qty,
      memType: form.memType,
      pkgType: form.pkgType,
      trainer: form.trainer,
      trainerPkgType: form.trainerPkgType,
      Validilty: form.Validilty,
      total_fee: `${basePrice}`,
      Group: form.Group,
      ptPackages: form.ptPackages,
      time_slot: form.time_slot,
      Nutritionist: form.Nutritionist,
      NutritionistPkgType: form.NutritionistPkgType,
      order_id: form.order_id,
      duration: form.duration,
      start_date: form.sdate,
    };

    try {
      setLoading(true);

      if (selectedService === "1") {
        await UserServices.gymPkgSell(branch_id, id, endDate, basePrice, "1", {
          ...commonValues,
          gymPkgType: form.gymPkgType,
        });
      } else if (selectedService === "2") {
        await UserServices.trainerPkgSell(branch_id, id, endDate, basePrice, "2", commonValues);
      } else if (selectedService === "3") {
        await UserServices.guestPassSell(
          branch_id,
          id,
          basePrice,
          endDate,
          "3",
          commonValues,
          fixedPackageId
        );
      } else if (selectedService === "4") {
        await UserServices.smallGroupSell(branch_id, id, endDate, "4", commonValues);
      } else if (selectedService === "5") {
        await UserServices.NutritionistPkgSell(
          branch_id,
          id,
          basePrice,
          endDate,
          "5",
          commonValues
        );
      } else if (selectedService === "6") {
        await UserServices.registerPkgSell(branch_id, id, basePrice, "6", commonValues);
      } else if (selectedService === "7") {
        await UserServices.sessionPkgSell(
          branch_id,
          id,
          basePrice,
          endDate,
          duration,
          "7",
          commonValues
        );
      } else if (selectedService === "8") {
        await UserServices.freezingPkgSell(
          commonValues,
          id,
          branch_id,
          basePrice,
          fixedPackageId,
          "8",
          endDate
        );
      } else if (selectedService === "9") {
        await UserServices.genralPkgSell(branch_id, id, basePrice, "9", commonValues);
      } else if (selectedService === "11") {
        await UserServices.CFTpkgSell(branch_id, id, endDate, basePrice, "11", commonValues);
      } else if (selectedService === "12") {
        await UserServices.CFTpkgSell(branch_id, id, endDate, basePrice, "12", commonValues);
      } else if (selectedService === "14") {
        await UserServices.physioPkgSell(branch_id, id, endDate, basePrice, "14", commonValues);
      } else if (selectedService === "15") {
        await UserServices.sessionPkgSell(
          branch_id,
          id,
          basePrice,
          endDate,
          duration,
          "15",
          commonValues
        );
      } else if (selectedService === "16") {
        await UserServices.befitPkgSell(branch_id, id, endDate, "16", commonValues, fixedPackageId);
      }

      toast.success(`${getServiceLabel(selectedService)} added successfully!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });

      await Promise.all([getCart(), getCatagriesAdded()]);
      setLoading(false);
    } catch (error) {
      console.log(error);
      toast.error("Unproccessable content!", { position: "top-center" });
      setLoading(false);
    }
  };

  const delEntry = (record_id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this record ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ed1c24",
      cancelButtonColor: "#303030",
      confirmButtonText: "Yes, Delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteRecord(record_id);
      }
    });
  };

  const deleteRecord = async (record_id) => {
    try {
      setLoading(true);
      const response = await UserServices.deleteClientCartItem(record_id);

      if (response.status === 204) {
        Swal.fire({
          title: "Success!",
          text: "Record deleted successfully.",
          icon: "success",
          confirmButtonColor: "#ed1c24",
        });
        await Promise.all([getCart(), getCatagriesAdded()]);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const destroyCart = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this record ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ed1c24",
      cancelButtonColor: "#303030",
      confirmButtonText: "Yes, Delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        allDestroy(id);
      }
    });
  };

  const allDestroy = async (clientId) => {
    try {
      setLoading(true);
      const response = await UserServices.cartDestroy(clientId);

      if (response.status === 204) {
        Swal.fire({
          title: "Success!",
          text: "Record deleted successfully.",
          icon: "success",
          confirmButtonColor: "#ed1c24",
        });
        await Promise.all([getCart(), getCatagriesAdded()]);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const Table = ({ data, rowsPerPage }) => {
    const { slice } = useTable(data, 1, rowsPerPage);
    const { items, requestSort, sortConfig } = useSortableData(slice);

    const getClassNamesFor = (name) => {
      if (!sortConfig) {
        return undefined;
      }

      return sortConfig.key === name ? sortConfig.direction : undefined;
    };

    return (
      <table className="table table-striped table-borderless table-style-1 mb-0">
        <thead>
          <tr>
            <th scope="col" className="no_sort">
              Sr#
            </th>
            <th
              scope="col"
              onClick={() => requestSort("client_fname")}
              className={getClassNamesFor("client_fname")}
            >
              Client Name
            </th>
            <th
              scope="col"
              onClick={() => requestSort("package_name")}
              className={getClassNamesFor("package_name")}
            >
              Package Name
            </th>
            <th
              scope="col"
              onClick={() => requestSort("price")}
              className={getClassNamesFor("price")}
            >
              Package Price
            </th>
            <th
              scope="col"
              onClick={() => requestSort("discount")}
              className={getClassNamesFor("discount")}
            >
              Discount
            </th>
            <th
              scope="col"
              onClick={() => requestSort("tax")}
              className={getClassNamesFor("tax")}
            >
              GST
            </th>
            <th
              scope="col"
              onClick={() => requestSort("net_price")}
              className={getClassNamesFor("net_price")}
            >
              Total Price
            </th>
            <th scope="col" className="no_sort">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {items.length ? (
            items.map((el, index) => (
              <tr key={el.id || index}>
                <td>{index + 1}</td>
                <td>{el.name}</td>
                <td>
                  {el.package_name !== null ? el.package_name : "Guest Pass"}
                  {el.note === "" ? null : (
                    <>
                      <i
                        className="bi bi-info-square note-here"
                        data-tooltip-id={el.id}
                        data-tooltip-content={el.note}
                      ></i>
                      <Tooltip id={el.id} place="right" className="note-tooltip" clickable />
                    </>
                  )}
                </td>
                <td>
                  <CurrencyFormat
                    value={el.price}
                    displayType={"text"}
                    thousandSeparator={true}
                    thousandSpacing={"2s"}
                    prefix={"Rs "}
                    suffix={"/-"}
                    renderText={(value) => <div>{value}</div>}
                  />
                </td>
                <td>
                  <CurrencyFormat
                    value={el.discount}
                    displayType={"text"}
                    thousandSeparator={true}
                    thousandSpacing={"2s"}
                    prefix={"Rs "}
                    suffix={"/-"}
                    renderText={(value) => <div>{value}</div>}
                  />
                </td>
                <td>
                  <CurrencyFormat
                    value={el.tax}
                    displayType={"text"}
                    thousandSeparator={true}
                    thousandSpacing={"2s"}
                    prefix={"Rs "}
                    suffix={"/-"}
                    renderText={(value) => <div>{value}</div>}
                  />
                </td>
                <td>
                  <CurrencyFormat
                    value={el.net_price}
                    displayType={"text"}
                    thousandSeparator={true}
                    thousandSpacing={"2s"}
                    prefix={"Rs "}
                    suffix={"/-"}
                    renderText={(value) => <div>{value}</div>}
                  />
                </td>
                <td>
                  <button
                    className="text-danger custom-button-image"
                    type="button"
                    onClick={() => delEntry(el.id)}
                  >
                    <i className="bi bi-trash-fill"></i>
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="14" className="text-center text-capitalize">
                No record found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  const addedCategories = Object.values(catagrieAdded || {}).map((value) => `${value}`);
  const availableServices = SERVICE_OPTIONS.filter((service) => !addedCategories.includes(service.id));
  const queryString = `?saleType=${saleType}`;

  const renderCommonDiscountFields = () => (
    <>
      <div className="col-12 col-md-4">
        <div className="form-group custom-group">
          <label className="form-label">
            Discount Type <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="discount_type"
            value={form.discount_type}
            onChange={onDiscountTypeChange}
          >
            <option value="Amount">Amount</option>
            <option value="Percentage">Percentage</option>
          </select>
        </div>
      </div>
      <div className="col-12 col-md-4">
        <div className="form-group custom-group">
          <label className="form-label">
            Discount <span className="text-danger">*</span>
          </label>
          <input
            onWheel={(event) => event.target.blur()}
            className="form-control"
            type="number"
            name="discount_amount"
            value={form.discount_amount}
            onChange={onDiscountAmountChange}
          />
          {fieldErrors.discount_amount ? (
            <p className="text-danger">{fieldErrors.discount_amount}</p>
          ) : null}
        </div>
      </div>
      <div className="col-12 col-md-4">
        <div className="form-group custom-group">
          <label className="form-label">Pay After Discount</label>
          <input className="form-control" type="number" value={form.pay_after_dis} disabled />
        </div>
      </div>
    </>
  );

  const renderApprovalAndNotes = (includeApprove = true, includeReference = true) => (
    <>
      {includeApprove ? (
        <div className="col-12 col-md-4">
          <div className="form-group custom-group">
            <label className="form-label">Approve By</label>
            <input
              className="form-control"
              type="text"
              name="approveBy"
              value={form.approveBy}
              onChange={onSimpleFieldChange}
            />
          </div>
        </div>
      ) : null}
      {includeReference ? (
        <div className="col-12 col-md-4">
          <div className="form-group custom-group">
            <label className="form-label">Referenced By</label>
            <input
              className="form-control"
              type="text"
              name="referencedBy"
              value={form.referencedBy}
              onChange={onSimpleFieldChange}
            />
          </div>
        </div>
      ) : null}
      <div className="col-12 col-md-4">
        <div className="form-group custom-group">
          <label className="form-label">Sales Notes</label>
          <input
            className="form-control"
            type="text"
            name="salesnotes"
            value={form.salesnotes}
            onChange={onSimpleFieldChange}
          />
        </div>
      </div>
      <div className="col-12 col-md-4">
        <div className="form-group custom-group">
          <label className="form-label">Sale Date</label>
          <input
            className="form-control"
            type="date"
            name="salesdate"
            value={form.salesdate}
            onChange={onSimpleFieldChange}
          />
          {fieldErrors.salesdate ? <p className="text-danger">{fieldErrors.salesdate}</p> : null}
        </div>
      </div>
    </>
  );

  const renderSelectedServiceFields = () => {
    if (!selectedService) {
      return null;
    }

    if (serviceMessage) {
      return (
        <div className="col-12">
          <div className="alert alert-warning mb-0">{serviceMessage}</div>
        </div>
      );
    }

    if (selectedService === "1") {
      return (
        <>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Membership Type <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="memType" value={form.memType} onChange={onGymMembershipChange}>
                {primaryOptions.length ? primaryOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Membership Type</option>}
              </select>
              {fieldErrors.memType ? <p className="text-danger">{fieldErrors.memType}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Gym Package <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="gymPkgType" value={form.gymPkgType} onChange={onGymPackageChange}>
                {secondaryOptions.length ? secondaryOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Gym Package</option>}
              </select>
              {fieldErrors.gymPkgType ? <p className="text-danger">{fieldErrors.gymPkgType}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Start Date <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="date" name="sdate" value={form.sdate} onChange={onStartDateChange} />
              {fieldErrors.sdate ? <p className="text-danger">{fieldErrors.sdate}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">End Date</label>
              <input className="form-control" type="date" value={endDate} disabled />
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Package Full Price <span className="text-danger">*</span>
              </label>
              <input
                onWheel={(event) => event.target.blur()}
                className="form-control"
                type="number"
                value={basePrice}
                onChange={onEditablePriceChange}
              />
              {fieldErrors.price ? <p className="text-danger">{fieldErrors.price}</p> : null}
            </div>
          </div>
          {renderCommonDiscountFields()}
          {renderApprovalAndNotes()}
        </>
      );
    }

    if (selectedService === "2") {
      return (
        <>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Available Trainers <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="trainer" value={form.trainer} onChange={onProviderChange}>
                {providerOptions.length ? providerOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Trainer</option>}
              </select>
              {fieldErrors.trainer ? <p className="text-danger">{fieldErrors.trainer}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Package Type <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                name="trainerPkgType"
                value={form.trainerPkgType}
                onChange={onProviderPackageTypeChange}
              >
                {secondaryOptions.length ? secondaryOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Package Type</option>}
              </select>
              {fieldErrors.trainerPkgType ? (
                <p className="text-danger">{fieldErrors.trainerPkgType}</p>
              ) : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Package <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="pkgType" value={form.pkgType} onChange={onTrainerPackageChange}>
                {tertiaryOptions.length ? tertiaryOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Package</option>}
              </select>
              {fieldErrors.pkgType ? <p className="text-danger">{fieldErrors.pkgType}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Start Date <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="date" name="sdate" value={form.sdate} onChange={onStartDateChange} />
              {fieldErrors.sdate ? <p className="text-danger">{fieldErrors.sdate}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">End Date</label>
              <input className="form-control" type="date" value={endDate} disabled />
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Quantity <span className="text-danger">*</span>
              </label>
              <input
                onWheel={(event) => event.target.blur()}
                className="form-control"
                type="number"
                name="qty"
                value={form.qty}
                onChange={onTrainerQuantityChange}
              />
              {fieldErrors.qty ? <p className="text-danger">{fieldErrors.qty}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">Package Full Price</label>
              <input className="form-control" type="number" value={basePrice} disabled />
            </div>
          </div>
          {renderCommonDiscountFields()}
          {renderApprovalAndNotes()}
        </>
      );
    }

    if (selectedService === "3") {
      return (
        <>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Validity <span className="text-danger">*</span>
              </label>
              <input
                onWheel={(event) => event.target.blur()}
                className="form-control"
                type="number"
                name="Validilty"
                value={form.Validilty}
                onChange={onGuestValidityChange}
              />
              {fieldErrors.Validilty ? <p className="text-danger">{fieldErrors.Validilty}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Total Fee <span className="text-danger">*</span>
              </label>
              <input
                onWheel={(event) => event.target.blur()}
                className="form-control"
                type="number"
                value={basePrice}
                onChange={onGuestPriceChange}
              />
              {fieldErrors.total_fee ? <p className="text-danger">{fieldErrors.total_fee}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Start Date <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="date" name="sdate" value={form.sdate} onChange={onStartDateChange} />
              {fieldErrors.sdate ? <p className="text-danger">{fieldErrors.sdate}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">End Date</label>
              <input className="form-control" type="date" value={endDate} disabled />
            </div>
          </div>
          {renderCommonDiscountFields()}
          {renderApprovalAndNotes()}
        </>
      );
    }

    if (selectedService === "4") {
      return (
        <>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Group <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="Group" value={form.Group} onChange={onGroupChange}>
                <option value="">Select Group</option>
                {GROUP_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
              {fieldErrors.Group ? <p className="text-danger">{fieldErrors.Group}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Select Package <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="ptPackages" value={form.ptPackages} onChange={onGroupPackageChange}>
                {secondaryOptions.length ? secondaryOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Please select a group to view packages</option>}
              </select>
              {fieldErrors.ptPackages ? <p className="text-danger">{fieldErrors.ptPackages}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Time Slot <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="time_slot" value={form.time_slot} onChange={onTimeSlotChange}>
                {timeSlotOptions.length ? timeSlotOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Time Slot</option>}
              </select>
              {fieldErrors.time_slot ? <p className="text-danger">{fieldErrors.time_slot}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Start Date <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="date" name="sdate" value={form.sdate} onChange={onStartDateChange} />
              {fieldErrors.sdate ? <p className="text-danger">{fieldErrors.sdate}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">End Date</label>
              <input className="form-control" type="date" value={endDate} disabled />
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Total Fee <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="number" value={basePrice} disabled />
              {fieldErrors.total_fee ? <p className="text-danger">{fieldErrors.total_fee}</p> : null}
            </div>
          </div>
          {renderCommonDiscountFields()}
          {renderApprovalAndNotes()}
        </>
      );
    }

    if (selectedService === "5") {
      return (
        <>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Available Nutritionists <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="Nutritionist" value={form.Nutritionist} onChange={onProviderChange}>
                {providerOptions.length ? providerOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Nutritionist</option>}
              </select>
              {fieldErrors.Nutritionist ? (
                <p className="text-danger">{fieldErrors.Nutritionist}</p>
              ) : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Package Type <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                name="NutritionistPkgType"
                value={form.NutritionistPkgType}
                onChange={onProviderPackageTypeChange}
              >
                {secondaryOptions.length ? secondaryOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Package Type</option>}
              </select>
              {fieldErrors.NutritionistPkgType ? (
                <p className="text-danger">{fieldErrors.NutritionistPkgType}</p>
              ) : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Package <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="pkgType" value={form.pkgType} onChange={onTrainerPackageChange}>
                {tertiaryOptions.length ? tertiaryOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Package</option>}
              </select>
              {fieldErrors.pkgType ? <p className="text-danger">{fieldErrors.pkgType}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Start Date <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="date" name="sdate" value={form.sdate} onChange={onStartDateChange} />
              {fieldErrors.sdate ? <p className="text-danger">{fieldErrors.sdate}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">End Date</label>
              <input className="form-control" type="date" value={endDate} disabled />
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">Package Full Price</label>
              <input className="form-control" type="number" value={basePrice} disabled />
            </div>
          </div>
          {renderCommonDiscountFields()}
          {renderApprovalAndNotes()}
        </>
      );
    }

    if (selectedService === "6") {
      return (
        <>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Membership Type <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="memType" value={form.memType} onChange={onRegistrationPackageChange}>
                {primaryOptions.length ? primaryOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Membership Type</option>}
              </select>
              {fieldErrors.memType ? <p className="text-danger">{fieldErrors.memType}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Package Full Price (Editable) <span className="text-danger">*</span>
              </label>
              <input
                onWheel={(event) => event.target.blur()}
                className="form-control"
                type="number"
                value={basePrice}
                onChange={onEditablePriceChange}
              />
              {fieldErrors.price ? <p className="text-danger">{fieldErrors.price}</p> : null}
            </div>
          </div>
          {renderCommonDiscountFields()}
          {renderApprovalAndNotes()}
        </>
      );
    }

    if (
      selectedService === "7" ||
      selectedService === "11" ||
      selectedService === "12" ||
      selectedService === "14" ||
      selectedService === "15"
    ) {
      const selectLabel =
        selectedService === "7"
          ? "Session"
          : selectedService === "15"
            ? "Class"
            : "Package";

      return (
        <>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Select {selectLabel} <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="memType" value={form.memType} onChange={onDatePackageChange}>
                {primaryOptions.length ? primaryOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select {selectLabel}</option>}
              </select>
              {fieldErrors.memType ? <p className="text-danger">{fieldErrors.memType}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Start Date <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="date" name="sdate" value={form.sdate} onChange={onStartDateChange} />
              {fieldErrors.sdate ? <p className="text-danger">{fieldErrors.sdate}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">End Date</label>
              <input className="form-control" type="date" value={endDate} disabled />
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Package Full Price <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="number" value={basePrice} disabled />
              {fieldErrors.price ? <p className="text-danger">{fieldErrors.price}</p> : null}
            </div>
          </div>
          {renderCommonDiscountFields()}
          {renderApprovalAndNotes()}
        </>
      );
    }

    if (selectedService === "8") {
      return (
        <>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Order Name <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="order_id" value={form.order_id} onChange={onSimpleFieldChange}>
                {freezingOrders.length ? freezingOrders.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Order</option>}
              </select>
              {fieldErrors.order_id ? <p className="text-danger">{fieldErrors.order_id}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">Package Name</label>
              <input className="form-control" type="text" value={fixedPackageName || "Freezing"} disabled />
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Freezing Fee <span className="text-danger">*</span>
              </label>
              <input
                onWheel={(event) => event.target.blur()}
                className="form-control"
                type="number"
                value={basePrice}
                onChange={(event) => updateBasePrice(event.target.value)}
              />
              {fieldErrors.price ? <p className="text-danger">{fieldErrors.price}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Number of Days <span className="text-danger">*</span>
              </label>
              <input
                onWheel={(event) => event.target.blur()}
                className="form-control"
                type="number"
                name="duration"
                value={form.duration}
                onChange={onFreezingDurationChange}
              />
              {fieldErrors.duration ? <p className="text-danger">{fieldErrors.duration}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Start Date <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="date" name="sdate" value={form.sdate} onChange={onStartDateChange} />
              {fieldErrors.sdate ? <p className="text-danger">{fieldErrors.sdate}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">End Date</label>
              <input className="form-control" type="date" value={endDate} disabled />
            </div>
          </div>
          {renderCommonDiscountFields()}
          {renderApprovalAndNotes(false, false)}
        </>
      );
    }

    if (selectedService === "9") {
      return (
        <>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Package <span className="text-danger">*</span>
              </label>
              <select className="form-select" name="memType" value={form.memType} onChange={onRegistrationPackageChange}>
                {primaryOptions.length ? primaryOptions.map((option) => (
                  <option key={option.id || "default"} value={option.id}>
                    {option.name}
                  </option>
                )) : <option value="">Select Package</option>}
              </select>
              {fieldErrors.memType ? <p className="text-danger">{fieldErrors.memType}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Package Full Price <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="number" value={basePrice} disabled />
              {fieldErrors.price ? <p className="text-danger">{fieldErrors.price}</p> : null}
            </div>
          </div>
          {renderCommonDiscountFields()}
          {renderApprovalAndNotes()}
        </>
      );
    }

    if (selectedService === "16") {
      return (
        <>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Package <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="text" value={fixedPackageName} disabled />
              {fieldErrors.pkgType ? <p className="text-danger">{fieldErrors.pkgType}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">
                Start Date <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="date" name="sdate" value={form.sdate} onChange={onStartDateChange} />
              {fieldErrors.sdate ? <p className="text-danger">{fieldErrors.sdate}</p> : null}
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group custom-group">
              <label className="form-label">End Date</label>
              <input className="form-control" type="date" value={endDate} disabled />
            </div>
          </div>
          {renderApprovalAndNotes(false, true)}
        </>
      );
    }

    return null;
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="card">
            {cart ? (
              <>
                <div className="card-header py-3">
                  <h5 className="mb-0 font-20">Invoice </h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <Table data={myData} rowsPerPage={limit} />
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="grand-total-cart">
                        <div className="cart-entry">
                          Total Price{" "}
                          <CurrencyFormat
                            value={myData.reduce((a, v) => (a = a + v.price), 0)}
                            displayType={"text"}
                            thousandSpacing={"2s"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                            suffix={"/-"}
                            renderText={(value) => <span>{value}</span>}
                          />
                          <div className="clearfix"></div>
                        </div>
                        <div className="cart-entry">
                          Total Discount{" "}
                          <CurrencyFormat
                            value={myData.reduce((a, v) => (a = a + v.discount), 0)}
                            displayType={"text"}
                            thousandSpacing={"2s"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                            suffix={"/-"}
                            renderText={(value) => <span>{value}</span>}
                          />
                          <div className="clearfix"></div>
                        </div>
                        <div className="cart-entry">
                          GST{" "}
                          <CurrencyFormat
                            value={myData.reduce((a, v) => (a = a + v.tax), 0)}
                            displayType={"text"}
                            thousandSpacing={"2s"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                            suffix={"/-"}
                            renderText={(value) => <span>{value}</span>}
                          />
                          <div className="clearfix"></div>
                        </div>
                        <div className="cart-entry">
                          Grand Total{" "}
                          <CurrencyFormat
                            value={myData.reduce((a, v) => (a = a + v.net_price), 0)}
                            displayType={"text"}
                            thousandSpacing={"2s"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                            suffix={"/-"}
                            renderText={(value) => <span>{value}</span>}
                          />
                          <div className="clearfix"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grand-totl-fine">
                    <div className="col-md-7"></div>
                    <button
                      type="button"
                      className="btn btn-primary cancel-order px-4"
                      onClick={destroyCart}
                    >
                      Cancel
                    </button>
                    <Link
                      to={`/check_out/${id}${queryString}`}
                      className="btn btn-primary px-4 ms-2 addclient-button-custom"
                    >
                      Pay Now
                    </Link>
                  </div>
                </div>
              </>
            ) : null}

            <div className="card-header py-3">
              <div className="row">
                <div className="col-6">
                  <h5 className="mb-0 font-20">Package Sell</h5>
                </div>
                <div className="col-6">
                  <Link to={-1} className="btn btn-primary d-inline-block float-end btn-sm">
                    <i className="bi bi-arrow-left"></i> Back
                  </Link>
                </div>
              </div>
            </div>

            <div className="card-body">
              <p>
                <i
                  className="bi bi-exclamation-lg"
                  style={{ color: "red" }}
                  aria-hidden="true"
                />
                The Fields With <strong style={{ color: "red" }}>*</strong> Must Required Or Fill.
              </p>

              <div className="row g-3 trainee-add">
                <div className="col-12 col-md-4">
                  <div className="form-group custom-group">
                    <label className="form-label">Client</label>
                    <input className="form-control" type="text" value={clientName} disabled />
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <div className="form-group custom-group">
                    <label className="form-label">
                      Sale Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={saleType}
                      onChange={(event) => {
                        setSaleType(event.target.value);
                        setFieldErrors((prev) => ({ ...prev, saleType: "" }));
                      }}
                    >
                      <option value="New">New</option>
                      <option value="Renew">Renew</option>
                    </select>
                    {fieldErrors.saleType ? <p className="text-danger">{fieldErrors.saleType}</p> : null}
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <div className="form-group custom-group">
                    <label className="form-label">
                      Select Service <span className="text-danger">*</span>
                    </label>
                    {availableServices.length ? (
                      <select className="form-select" value={selectedService} onChange={onServiceChange}>
                        <option value="">Select Service</option>
                        {availableServices.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select className="form-select" disabled>
                        <option value="">All Packages Sold</option>
                      </select>
                    )}
                    {fieldErrors.service ? <p className="text-danger">{fieldErrors.service}</p> : null}
                  </div>
                </div>

                {renderSelectedServiceFields()}
              </div>
            </div>

            {selectedService ? (
              <div className="col-12 text-center mb-3">
                <button
                  type="button"
                  className="btn btn-primary px-5 mb-1 me-2 addclient-button"
                  onClick={addSelectedService}
                >
                  Add Package
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </main>

      <div className={`loader ${loading ? "in" : ""}`}>
        <div className="spinner-border main-spin"></div>
      </div>
    </div>
  );
}
